<template>
  <div class="booking-form">
    <div v-if="readOnly" class="readonly-banner">
      <strong>This booking is {{ bookingStatus }}.</strong> It can no longer be modified.
    </div>

    <!-- Section: {{ $t('secBooking') }} -->
    <section class="form-section">
      <div class="section-heading">{{ $t('secBooking') }}</div>
      <div class="section-body">
        <StoreSelector :value="form.store_id" @change="onStoreChange" @loaded="onStoresLoaded" />
        <DateRangePicker
          :store-id="form.store_id"
          :drop-off-date="form.drop_off_date"
          :pick-up-date="form.pick_up_date"
          :timezone="selectedStoreTimezone"
          @change="onDatesChange"
          @valid="onDatesValid"
        />
        <LuggageSelector
          :store-id="form.store_id"
          :value="form.luggage"
          :fee-options="feeOptions"
          :drop-off-date="form.drop_off_date"
          :pick-up-date="form.pick_up_date"
          @change="onLuggageChange"
        />
        <FeeEstimate :estimate="estimate" />
        <div class="field email-field">
          <label class="field-label">Email <span class="req">*</span></label>
          <input
            type="email"
            class="field-input"
            :class="{ 'has-error': emailError }"
            :value="form.passenger.email"
            @input="onEmailChange($event.target.value)"
            @blur="emailTouched = true"
            placeholder="jane@example.com"
          />
          <p v-if="emailError" class="field-error">{{ emailError }}</p>
        </div>
      </div>
    </section>

    <!-- Section: {{ $t('passengerInfo') }} -->
    <section v-show="showDetailSections" class="form-section">
      <div class="section-heading">{{ $t('passengerInfo') }}</div>
      <div class="section-body">
        <PassengerInfo ref="passengerInfo" :value="form.passenger" :hide-email="true" :require-identity="submitIncludesDetails" @change="onPassengerChange" />
      </div>
    </section>

    <!-- Section: {{ $t('thirdParty') }} -->
    <section v-show="showDetailSections" class="form-section">
      <div class="section-heading">{{ $t('thirdParty') }}</div>
      <div class="section-body">
        <ThirdPartyPickup
          :value="form.thirdParty"
          :enabled="form.thirdPartyEnabled"
          @change="onThirdPartyChange"
        />
      </div>
    </section>

    <!-- Section: {{ $t('hazardous') }} -->
    <section v-show="showDetailSections" class="form-section">
      <div class="section-heading">{{ $t('hazardous') }}</div>
      <div class="section-body">
        <HazardousDeclaration ref="hazardous" @valid="onHazardValid" @change="onHazardChange" />
      </div>
    </section>

    <!-- Section: {{ $t('legal') }} -->
    <section v-show="showDetailSections" class="form-section">
      <div class="section-heading">{{ $t('legal') }}</div>
      <div class="section-body">
        <LegalAgreement
          ref="legal"
          :value="hasSignature"
          :store-id="form.store_id"
          :stores="stores"
          @signature="onSignature"
          @clear="onSignatureClear"
          @terms-change="onTermsChange"
        />
        <div v-if="existingSignatureUrl && !signatureData" class="existing-signature">
          <div class="existing-signature-label">Current signature on file:</div>
          <img :src="'/photo/' + existingSignatureUrl" alt="Existing signature" />
        </div>
      </div>
    </section>

    <!-- Validation Summary -->
    <transition name="msg-fade">
      <div v-if="submitError" class="error-banner">{{ submitError }}</div>
    </transition>

    <!-- Submit -->
    <button
      type="button"
      class="btn-submit"
      :disabled="!canSubmit"
      @click="submit"
    >
      <span v-if="submitting" class="spinner"></span>
      {{ submitting ? $t('submitting') : submitButtonLabel }}
    </button>
  </div>
</template>

<script>
import bookingApi from '../api/booking'
import StoreSelector from './StoreSelector.vue'
import DateRangePicker from './DateRangePicker.vue'
import LuggageSelector from './LuggageSelector.vue'
import FeeEstimate from './FeeEstimate.vue'
import PassengerInfo from './PassengerInfo.vue'
import ThirdPartyPickup from './ThirdPartyPickup.vue'
import HazardousDeclaration from './HazardousDeclaration.vue'
import LegalAgreement from './LegalAgreement.vue'
import countryCodes from '../constants/countryCodes'
import { resolveStoreIdFromWpParams } from '@/utils/wpStoreResolver'
import { parseWpHandoffParams } from '../utils/embedContext'

export default {
  name: 'BookingForm',
  inject: {
    embedMode: { default: false }
  },
  components: {
    StoreSelector, DateRangePicker, LuggageSelector, FeeEstimate,
    PassengerInfo, ThirdPartyPickup, HazardousDeclaration, LegalAgreement
  },
  props: {
    token: { type: String, default: null }
  },
  data () {
    return {
      form: {
        store_id: null,
        drop_off_date: '',
        pick_up_date: '',
        luggage: [],
        passenger: {
          first_name: '', last_name: '', email: '',
          mobile_prefix: '+61', mobile_number: '',
          passport: '', licence: '', flight_no: '', travel_date: ''
        },
        thirdPartyEnabled: false,
        thirdParty: {}
      },
      feeOptions: [],
      estimate: null,
      hazardousValid: false,
      hazardousItems: [],
      hasSignature: false,
      signatureData: null,
      existingSignatureUrl: null,
      submitting: false,
      submitError: '',
      readOnly: false,
      bookingStatus: null,
      stores: [],
      bookingTimezone: 'Australia/Sydney',
      emailTouched: false,
      detailsCompleted: false,
      hazardousTouched: false,
      termsAccepted: false,
      datesValid: true,
      pendingWpHandoff: null
    }
  },
  computed: {
    showDetailSections () {
      return !this.embedMode || !!this.token
    },
    submitIncludesDetails () {
      return !this.embedMode || !!this.token
    },
    submitButtonLabel () {
      if (this.embedMode && !this.token) return this.$t('submit')
      return this.$t('saveBooking')
    },
    selectedStoreTimezone () {
      var row = (this.stores || []).find(s => Number(s.id) === Number(this.form.store_id))
      return (row && row.timezone) || 'Australia/Sydney'
    },
    emailValid () {
      const email = (this.form.passenger.email || '').trim()
      return email !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },
    emailError () {
      if (!this.emailTouched) return ''
      const email = (this.form.passenger.email || '').trim()
      if (!email) return 'Email ' + this.$t('required')
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email.'
      return ''
    },
    canSubmit () {
      const stage1Ready = (
        this.form.store_id &&
        this.form.drop_off_date &&
        this.form.pick_up_date &&
        this.datesValid &&
        this.form.luggage.length > 0 &&
        this.form.luggage.every(item => !!item.size) &&
        this.form.luggage.every(item => (parseInt(item.quantity, 10) || 0) > 0) &&
        this.emailValid
      )

      if (!this.submitIncludesDetails) {
        return !this.submitting && stage1Ready
      }

      return !this.submitting && !this.readOnly && stage1Ready && this.hazardousValid && this.termsAccepted
    }
  },
  created () {
    var handoff = parseWpHandoffParams()
    if (handoff.airport) {
      this.pendingWpHandoff = handoff
    }
    if (this.token) this.loadBooking()
  },
  mounted () {
    this._onBridgeSetStore = (event) => {
      if (this.token || this.readOnly) return
      var storeId = event.detail && event.detail.storeId
      if (storeId) this.onStoreChange(Number(storeId))
    }
    this._onBridgeSetAirport = (event) => {
      if (this.token || this.readOnly) return
      var detail = event.detail || {}
      if (detail.airport) {
        this.pendingWpHandoff = {
          country: detail.country || '',
          airport: String(detail.airport).toUpperCase(),
          terminal: detail.terminal ? String(detail.terminal).toUpperCase() : '',
          service: detail.service || ''
        }
      }
      this.applyWpStoreSelection()
    }
    window.addEventListener('secure-travel:set-store', this._onBridgeSetStore)
    window.addEventListener('secure-travel:set-airport', this._onBridgeSetAirport)
  },
  beforeDestroy () {
    if (this._onBridgeSetStore) {
      window.removeEventListener('secure-travel:set-store', this._onBridgeSetStore)
    }
    if (this._onBridgeSetAirport) {
      window.removeEventListener('secure-travel:set-airport', this._onBridgeSetAirport)
    }
  },
  methods: {
    toDateTimeLocalValue (value, timezone) {
      if (!value) return ''
      const iso = String(value).replace(' ', 'T')
      const utcDate = new Date(iso.endsWith('Z') ? iso : `${iso}Z`)
      if (isNaN(utcDate.getTime())) return String(value).slice(0, 16)
      const tz = timezone || this.selectedStoreTimezone || 'Australia/Sydney'
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(utcDate)
      const pick = t => (parts.find(p => p.type === t) || {}).value || '00'
      return `${pick('year')}-${pick('month')}-${pick('day')}T${pick('hour')}:${pick('minute')}`
    },
    toDateOnlyValue (value, timezone) {
      if (!value) return ''
      return this.toDateTimeLocalValue(value, timezone).slice(0, 10)
    },
    splitPhone (rawValue) {
      const value = String(rawValue || '').trim()
      if (!value) return { prefix: '+61', number: '' }
      if (value[0] === '+') {
        const m = value.match(/^(\+\d+)\s*(.*)$/)
        if (m) return { prefix: m[1], number: m[2] || '' }
      }
      const digits = value.replace(/\D+/g, '')
      const codes = countryCodes.map(c => c.value.replace('+', '')).sort((a, b) => b.length - a.length)
      for (let i = 0; i < codes.length; i++) {
        const code = codes[i]
        if (digits.indexOf(code) === 0 && digits.length > code.length + 3) {
          return { prefix: `+${code}`, number: digits.slice(code.length) }
        }
      }
      return { prefix: '+61', number: value }
    },
    expandLuggageItems (items) {
      const expanded = []
      ;(items || []).forEach(item => {
        const qty = Math.max(1, parseInt(item.quantity, 10) || 1)
        for (let i = 0; i < qty; i++) expanded.push({ size: item.size })
      })
      return expanded
    },
    compressLuggageItems (items) {
      const grouped = {}
      ;(items || []).forEach((item, idx) => {
        if (!item || !item.size) return
        if (!grouped[item.size]) grouped[item.size] = { size: item.size, quantity: 0, _key: idx + 1 }
        grouped[item.size].quantity += 1
      })
      return Object.keys(grouped).map(k => grouped[k])
    },
    async loadBooking () {
      if (!this.token) return
      try {
        const { data } = await bookingApi.getBooking(this.token)
        const b = data.data || data
        this.bookingTimezone = b.booking_timezone || this.selectedStoreTimezone
        this.bookingStatus = b.status
        this.detailsCompleted = !!b.details_completed
        if (b.status && b.status !== 'pending') {
          this.readOnly = true
        }
        this.form.store_id = b.store_id
        await this.loadStoreFees()
        this.form.drop_off_date = this.toDateTimeLocalValue(b.drop_off_date, this.bookingTimezone)
        this.form.pick_up_date = this.toDateTimeLocalValue(b.pick_up_date, this.bookingTimezone)
        this.form.luggage = this.compressLuggageItems(b.luggages || [])
        const passengerPhone = this.splitPhone(b.mobile_number)
        this.form.passenger = {
          first_name: b.first_name || '',
          last_name: b.last_name || '',
          email: b.email || '',
          mobile_prefix: passengerPhone.prefix || '+61',
          mobile_number: passengerPhone.number || '',
          passport: b.passport || '',
          licence: b.licence || '',
          flight_no: b.flight_no || '',
          travel_date: this.toDateOnlyValue(b.travel_date, this.bookingTimezone)
        }
        if (b.other_first_name) {
          this.form.thirdPartyEnabled = true
          this.form.thirdParty = {
            other_first_name: b.other_first_name || '',
            other_last_name: b.other_last_name || '',
            other_passport: b.other_passport || '',
            other_licence: b.other_licence || '',
            other_email: b.other_email || '',
            other_mobile_prefix: b.other_mobile_prefix || '+61',
            other_mobile_number: (b.other_mobile_number || '').replace(/^\+\d+\s*/, '')
          }
        }
        this.existingSignatureUrl = b.signature_url || null
        this.hasSignature = !!this.existingSignatureUrl
        this.hazardousTouched = b.hazardous_declaration !== null && b.hazardous_declaration !== undefined
        this.refreshEstimate()
      } catch (e) {
        this.submitError = 'Failed to load booking. Please try again.'
      }
    },
    onStoresLoaded (stores) {
      this.stores = Array.isArray(stores) ? stores : []
      var self = this
      this.$nextTick(function () {
        if (self.applyWpStoreSelection()) return
        if (!self.form.store_id && self.stores.length) {
          self.onStoreChange(Number(self.stores[0].id))
        }
      })
    },

    applyWpStoreSelection () {
      if (this.token) return false
      if (!this.stores.length) return false

      var handoff = this.pendingWpHandoff || parseWpHandoffParams()
      if (!handoff || !handoff.airport) return false

      var wpStoreId = resolveStoreIdFromWpParams(this.stores, handoff)
      console.log('wp store id', wpStoreId, handoff)
      if (!wpStoreId) return false

      if (Number(this.form.store_id) !== wpStoreId) {
        this.onStoreChange(wpStoreId)
      }
      return true
    },

    onStoreChange (id) {
      this.$set(this.form, 'store_id', id == null ? null : Number(id))
      this.form.luggage = []
      this.datesValid = true
      this.termsAccepted = false
      this.loadStoreFees()
      this.refreshEstimate()
    },
    onDatesChange ({ drop_off_date, pick_up_date }) {
      this.form.drop_off_date = drop_off_date
      this.form.pick_up_date = pick_up_date
      this.refreshEstimate()
    },
    onDatesValid (valid) {
      this.datesValid = !!valid
      if (!this.datesValid) this.estimate = null
    },
    onLuggageChange (items) {
      this.form.luggage = items
      this.refreshEstimate()
    },
    onPassengerChange (p) {
      this.form.passenger = p
    },
    onEmailChange (email) {
      this.form.passenger = Object.assign({}, this.form.passenger, { email })
    },
    onThirdPartyChange ({ enabled, data }) {
      this.form.thirdPartyEnabled = enabled
      this.form.thirdParty = data
    },
    onHazardValid (v) { this.hazardousValid = v },
    onHazardChange (v) {
      this.hazardousItems = Array.isArray(v) ? v : []
      this.hazardousTouched = true
    },
    hazardousDeclarationPayload () {
      if (this.$refs.hazardous && typeof this.$refs.hazardous.getDeclaration === 'function') {
        return this.$refs.hazardous.getDeclaration()
      }
      return Array.isArray(this.hazardousItems) ? this.hazardousItems.slice() : []
    },
    onSignature (dataUrl) {
      this.hasSignature = true
      this.signatureData = dataUrl
    },
    onSignatureClear () {
      this.hasSignature = false
      this.signatureData = null
      this.existingSignatureUrl = null
    },
    onTermsChange (accepted) {
      this.termsAccepted = !!accepted
    },

    async loadStoreFees () {
      if (!this.form.store_id) {
        this.feeOptions = []
        return
      }
      try {
        const res = await bookingApi.getStoreFees(this.form.store_id)
        const rows = res.data.data || res.data || []
        this.feeOptions = Array.isArray(rows) ? rows : []
      } catch (e) {
        this.feeOptions = []
      }
    },

    async refreshEstimate () {
      if (!this.form.store_id || !this.form.drop_off_date || !this.form.pick_up_date || !this.form.luggage.length) {
        this.estimate = null
        return
      }
      try {
        const { data } = await bookingApi.estimatePrice({
          store_id: this.form.store_id,
          drop_off_date: this.form.drop_off_date,
          pick_up_date: this.form.pick_up_date,
          luggages: this.expandLuggageItems(this.form.luggage)
        })
        this.estimate = data.data || data
      } catch (e) {
        this.estimate = null
      }
    },

    async submit () {
      this.submitError = ''
      this.emailTouched = true

      if (!this.emailValid) {
        this.submitError = this.emailError || ('Email ' + this.$t('required'))
        return
      }

      if (this.submitIncludesDetails) {
        if (this.$refs.passengerInfo && !this.$refs.passengerInfo.validate()) {
          this.submitError = this.$t('passengerInfo') + ': please fix errors.'
          return
        }
        if (this.$refs.hazardous) {
          const hazardCheck = this.$refs.hazardous.validate()
          if (!hazardCheck.valid) {
            this.submitError = hazardCheck.message
            return
          }
        }
        if (this.$refs.legal && !this.$refs.legal.validate()) {
          this.submitError = this.$t('termsConfirmRequired')
          return
        }
      }

      this.submitting = true

      try {
        let signatureUrl = this.existingSignatureUrl
        if (this.submitIncludesDetails && this.$refs.legal) {
          if (!this.$refs.legal.isEmpty()) {
            const blob = await new Promise(resolve => this.$refs.legal.toBlob(resolve))
            if (blob) {
              const { data: sigResp } = await bookingApi.uploadSignature(blob)
              const sigData = sigResp.data || sigResp
              signatureUrl = sigData.id || sigData.path
            }
          }
        }

        const payload = {
          store_id: this.form.store_id,
          email: (this.form.passenger.email || '').trim(),
          drop_off_date: this.form.drop_off_date,
          pick_up_date: this.form.pick_up_date,
          luggages: this.expandLuggageItems(this.form.luggage)
        }

        if (this.submitIncludesDetails) {
          var passengerPayload = Object.assign({}, this.form.passenger)
          if (passengerPayload.mobile_number) {
            passengerPayload.mobile_number = `${passengerPayload.mobile_prefix || '+61'} ${passengerPayload.mobile_number}`.trim()
          }
          delete passengerPayload.mobile_prefix
          Object.assign(payload, passengerPayload)

          if (signatureUrl) {
            payload.signature_url = String(signatureUrl)
          }

          payload.hazardous_declaration = this.hazardousDeclarationPayload()

          if (this.form.thirdPartyEnabled) {
            var thirdPartyPayload = Object.assign({}, this.form.thirdParty)
            if (thirdPartyPayload.other_mobile_number) {
              thirdPartyPayload.other_mobile_number = `${thirdPartyPayload.other_mobile_prefix || '+61'} ${thirdPartyPayload.other_mobile_number}`
            }
            delete thirdPartyPayload.other_mobile_prefix
            Object.assign(payload, thirdPartyPayload)
          }
        }

        let result
        if (this.token) {
          result = await bookingApi.updateBooking(this.token, payload)
          const booking = result.data.data || result.data
          this.detailsCompleted = !!booking.details_completed
          this.$router.push({ name: 'confirmation', params: { booking } })
        } else {
          result = await bookingApi.createBooking(payload)
          const booking = result.data.data || result.data
          this.$router.push({ name: 'confirmation', params: { booking } })
        }
      } catch (err) {
        const msg = err.response && err.response.data && (err.response.data.message || err.response.data.error)
        this.submitError = msg || 'Something went wrong. Please try again.'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.booking-form {
  padding-bottom: 40px;
}

.form-section {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  margin-bottom: 20px;
  overflow: hidden;
}

.section-heading {
  background: #1e336f;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: .5px;
  padding: 12px 16px;
}

.section-body {
  padding: 20px 16px 4px;
}

.existing-signature {
  margin-top: 10px;
  padding: 10px;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  background: #f9fafb;
}

.existing-signature-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}

.existing-signature img {
  display: block;
  max-width: 100%;
  max-height: 120px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  color: #991b1b;
  font-size: 14px;
  padding: 12px 14px;
  margin-bottom: 16px;
  line-height: 1.5;
}

.btn-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 50px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: #1e336f;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background .2s, opacity .2s;
}
.btn-submit:hover:not(:disabled) { background: #14244f; }
.btn-submit:disabled { opacity: .45; cursor: not-allowed; }

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin .6s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.readonly-banner {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  color: #92400e;
  font-size: 14px;
  padding: 12px 14px;
  margin-bottom: 20px;
  line-height: 1.5;
  text-align: center;
}

.msg-fade-enter-active, .msg-fade-leave-active { transition: opacity .25s; }
.msg-fade-enter, .msg-fade-leave-to { opacity: 0; }

.email-field { margin-top: 8px; margin-bottom: 8px; }
.field-label { display: block; font-size: 13px; font-weight: 600; color: #1e336f; margin-bottom: 6px; }
.req { color: #ef4444; }
.field-input {
  width: 100%; min-height: 44px; padding: 10px 12px; font-size: 15px;
  border: 1px solid #e8ecf1; border-radius: 8px; background: #fff; color: #1e336f;
  outline: none; transition: border-color .2s;
}
.field-input:focus { border-color: #1e336f; box-shadow: 0 0 0 3px rgba(30,51,111,.12); }
.field-input.has-error { border-color: #ef4444; }
.field-error { margin-top: 4px; font-size: 12px; color: #ef4444; }
</style>



// WEBPACK FOOTER //
// src/components/BookingForm.vue